import { useCallback } from "react"
import { Visibility } from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { type FragmentOf, graphql, readFragment } from "@/graphql"
import { isAgreementTerminated } from "../utils/isAgreementTerminated"
import { useIsLeaveSupplierProcessAgreement } from "./useIsLeaveSupplierProcessAgreement"

const graphQLFragments = {
    agreement: graphql(
        `
            fragment useFormatAgreementStatus_Agreement on AgreementType {
                status
                supplyPoint {
                    id
                }
                ...isAgreementTerminated_Agreement
                ...useIsLeaveSupplierProcessAgreement_Agreement
            }
        `,
        [
            isAgreementTerminated.fragments.agreement,
            useIsLeaveSupplierProcessAgreement.fragments.agreement,
        ]
    ),
}

type UseFormatAgreementStatusProps = {
    accountNumber: string
}

type FormatAgreementStatusProps = {
    agreement: FragmentOf<typeof graphQLFragments.agreement>
}

export function useFormatAgreementStatus({ accountNumber }: UseFormatAgreementStatusProps) {
    const t = useTranslations("useFormatAgreementStatus")
    const isLeaveSupplierProcessAgreement = useIsLeaveSupplierProcessAgreement({
        accountNumber,
    })

    return useCallback(
        (props: FormatAgreementStatusProps) => {
            const agreement = readFragment(graphQLFragments.agreement, props.agreement)

            if (isLeaveSupplierProcessAgreement({ agreement })) {
                return t.rich("status", {
                    status: "PENDING_TERMINATION",
                    desktopOnly: (chunks) => (
                        <Visibility hidden={{ base: true, md: false }}>{chunks}</Visibility>
                    ),
                    mobileOnly: (chunks) => (
                        <Visibility hidden={{ base: false, md: true }}>{chunks}</Visibility>
                    ),
                })
            }

            if (isAgreementTerminated({ agreement })) {
                return t("status", { status: "CLOSED" })
            }

            return t("status", {
                status: agreement.status,
            })
        },
        [isLeaveSupplierProcessAgreement, t]
    )
}

useFormatAgreementStatus.messages = ["useFormatAgreementStatus"] as const

useFormatAgreementStatus.fragments = graphQLFragments

useFormatAgreementStatus.queryOptions = {
    ...useIsLeaveSupplierProcessAgreement.queryOptions,
}
